import { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import SearchIcon from '@mui/icons-material/Search';
import {
	CircularProgress,
	InputAdornment,
	IconButton,
	FormHelperText,
	MenuItem,
} from '@mui/material';
import { colours } from '../../shared/colors';
import CreateQuoteSectionTitle from '../CreateQuoteSectionTitle';
import { useQuoteContext } from '../../shared/contexts/QuoteContext';
import {
	useGetClientByABNQuery,
	useLazyGetClientsByNameQuery,
	useLazyGetClientByABNQuery,
	useLazyGetClientByACNQuery,
	useGetClientByACNQuery,
	useLazyGetACNClientsByNameQuery,
} from '../../services/features/clientApi/clientApi';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { AbnClientDetails, AcnClient, ApiSearchTypes } from '../../shared/types/quotes';

const ABN_REGEX = /^\d{11,}$/;
const ACN_REGEX = /^\d{9,}$/;

const SearchCompanyField = ({ options }: { options: any }) => {
	const { quoteGroup, currentQuotes, isReferred, isDeclined } = useQuoteContext();

	const formik = useMemo(() => options, [options]);

	const [entityId, setEntityId] = useState(formik.values.entityGroup?.id);

	const ref = useRef<HTMLInputElement>(null);

	const searchType = formik.values?.entityGroup?.searchFlag;

	const acnEntityTypeCode =
		formik.values.entityGroup?.id === 1
			? 'APTY'
			: formik.values.entityGroup?.id === 2
			? 'APUB'
			: '';

	const readonly = isReferred || isDeclined || !searchType;

	const [searchText, setSearchText] = useState<string>('');
	const [resetKey, setResetKey] = useState<number>(0);

	// useEffect(() => {
	//     if (formik.values.companyName) {
	//         setSearchText(formik.values.companyName);
	//     }
	// }, []);

	const [shouldSearch, setShouldSearch] = useState(false);

	const [clientList, setClientList] = useState<any>([]);

	const businessNumber = formik?.values?.businessNumber;
	const acnBusinessNumber = formik?.values?.acnBusinessNumber;
	const companyName = formik?.values?.companyName;

	const clientAbnQuery = useGetClientByABNQuery(businessNumber ?? skipToken, {
		skip: !businessNumber || searchType !== ApiSearchTypes.ABN,
	});

	const clientAcnQuery = useGetClientByACNQuery(
		{ acn: acnBusinessNumber ?? skipToken, acnEntityTypeCode },
		{
			skip: !acnBusinessNumber || searchType !== ApiSearchTypes.ACN,
		}
	);

	const [triggerGetClientByABN, searchAbnQuery] = useLazyGetClientByABNQuery();
	const [triggerGetClientByACN, searchAcnQuery] = useLazyGetClientByACNQuery();
	const [triggerGetClientsByName, searchNameQuery] = useLazyGetClientsByNameQuery();
	const [triggerGetACNClientsByName, searchAcnNameQuery] = useLazyGetACNClientsByNameQuery();

	const searchLoading = useMemo(
		() =>
			searchAbnQuery.isLoading ||
			searchAbnQuery.isFetching ||
			searchAcnQuery.isLoading ||
			searchAcnQuery.isFetching ||
			searchNameQuery.isLoading ||
			searchNameQuery.isFetching ||
			searchAcnNameQuery.isLoading ||
			searchAcnNameQuery.isFetching,
		[
			searchAbnQuery.isLoading,
			searchAbnQuery.isFetching,
			searchAcnQuery.isLoading,
			searchAcnQuery.isFetching,
			searchNameQuery.isLoading,
			searchNameQuery.isFetching,
			searchAcnNameQuery.isLoading,
			searchAcnNameQuery.isFetching,
		]
	);

	useEffect(() => {
		if (formik.values?.entityGroup?.id !== entityId) {
			setEntityId(formik.values?.entityGroup?.id);
			clearSearch();
		}
	}, [formik.values?.entityGroup?.id]);

	const clearSearch = () => {
		if (!quoteGroup) {
			formik.setFieldValue('businessNumber', null);
			formik.setFieldValue('acnBusinessNumber', null);
			formik.setFieldValue('companyName', '');
			setSearchText('');
			setClientList([]);
			setResetKey((prev) => prev + 1);
		}
	};

	useEffect(() => {
		if (!shouldSearch) return;

		const isABNSearch = ABN_REGEX.test(searchText) && searchType === ApiSearchTypes.ABN;
		const isACNSearch = ACN_REGEX.test(searchText) && searchType === ApiSearchTypes.ACN;

		console.log('Trigger search', { isABNSearch, isACNSearch, searchText, searchType });
		if (isABNSearch) {
			// Trigger ABN search
			triggerGetClientByABN(searchText)
				.unwrap()
				.then((res) => setClientList([res]))
				.catch((error) => {
					console.log('triggerGetClientByABN', { searchText, error });
					setClientList([]);
				});
		} else if (isACNSearch) {
			// Trigger ACN search
			triggerGetClientByACN({ acn: searchText, acnEntityTypeCode })
				.unwrap()
				.then((res) => setClientList([res.result]))
				.catch((error) => {
					console.log('triggerGetClientByACN', { searchText, error });
					setClientList([]);
				});
		} else if (searchType === ApiSearchTypes.ABN) {
			// Trigger name search
			triggerGetClientsByName(searchText)
				.unwrap()
				.then((res) => setClientList(res.result))
				.catch((error) => {
					console.log('triggerGetClientsByName', { searchText, error });
					setClientList([]);
				});
		} else {
			// Trigger ACN name search
			triggerGetACNClientsByName({ clientName: searchText, acnEntityTypeCode })
				.unwrap()
				.then((res) => setClientList(res))
				.catch((error) => {
					console.log('triggerGetACNClientsByName', { searchText, error });
					setClientList([]);
				});
		}

		setShouldSearch(false);
	}, [
		searchText,
		shouldSearch,
		searchType,
		triggerGetClientByABN,
		triggerGetClientsByName,
		triggerGetClientByACN,
		triggerGetACNClientsByName,
		acnEntityTypeCode,
	]);

	const setClient = (clientData: AbnClientDetails | AcnClient) => {
		// console.log('CLIENT DATA', clientData);
		const organisationName =
			'organizationName' in clientData ? clientData?.organizationName : null;
		const fullName = 'fullName' in clientData ? clientData?.fullName : null;
		const givenName =
			'givenName' in clientData && 'familyName' in clientData
				? `${clientData?.givenName} ${clientData?.familyName}`
				: null;
		const nameValue = 'name' in clientData ? clientData?.name?.nameValue : null;

		const newName = organisationName ?? fullName ?? givenName ?? nameValue ?? '';

		const entityCode =
			typeof clientData?.type == 'object' ? clientData?.type?.code : clientData?.type;

		const streetName =
			'addressLine1' in clientData
				? clientData?.addressLine1
				: clientData?.address?.[0]?.addressLine ?? '';
		const city =
			'addressLine2' in clientData
				? clientData?.addressLine2
				: clientData?.address?.[0]?.locality ?? '';
		const postcode =
			'postcode' in clientData
				? clientData?.postcode
				: clientData?.address?.[0]?.postCode ?? '';
		const region =
			'stateCode' in clientData
				? clientData?.stateCode
				: clientData?.address?.[0]?.state ?? '';

		// const acnBusinessNumber =
		// 	'identifier' in clientData ? clientData?.identifier?.number : null;
		// const businessNumber = 'businessNumber' in clientData ? clientData?.businessNumber : null;

		// if (
		// 	(formik.values.acnBusinessNumber &&
		// 		acnBusinessNumber !== formik.values.acnBusinessNumber) ||
		// 	(formik.values.businessNumber && businessNumber !== formik.values.businessNumber)
		// ) {
		// 	//
		// }
		console.log('CLIENT', clientData, formik.values);

		if (
			formik.values.companyName !== newName ||
			!formik.values.streetName ||
			!formik.values.city ||
			!formik.values.postcode
		) {
			formik.setFieldValue('streetName', streetName);
			formik.setFieldValue('city', city);
			formik.setFieldValue('postcode', postcode);
			formik.setFieldValue('region', region);
		}
		formik.setFieldValue('companyName', newName);
		formik.setFieldValue('entityCode', entityCode);
	};

	useEffect(() => {
		if (clientAbnQuery.data && searchType === ApiSearchTypes.ABN) {
			const client = clientAbnQuery.data;
			setClientList([client]);
			formik.setFieldValue('companyName', client?.organizationName ?? client?.fullName);
			setSearchText(client?.organizationName ?? client?.fullName);
			setClient(client);
		} else if (clientAcnQuery.data && searchType === ApiSearchTypes.ACN) {
			const client = clientAcnQuery.data?.result;
			setClientList([client]);
			formik.setFieldValue('companyName', client?.name?.nameValue ?? '');
			setSearchText(client?.name?.nameValue ?? '');
			setClient(client);
			// } else if (!quoteGroup) {
			// 	setClientList([]);
		}
	}, [clientAbnQuery.data, clientAcnQuery.data, searchType]);

	const searchDisabled = useMemo(
		() => readonly || currentQuotes.length > 0,
		[readonly, currentQuotes]
	);

	const placeholder =
		(formik.values.companyName ?? '').length === 0
			? searchType == null
				? 'Select entity type in order to search entities'
				: `Search ${
						searchType === ApiSearchTypes.ABN ? 'ABN' : 'ACN'
				  } number and press enter`
			: searchAbnQuery.isError || searchNameQuery.isError || searchAcnQuery.isError
			? 'There was an error. Please try again.'
			: formik.values.companyName;

	useEffect(() => {
		if (!businessNumber && !acnBusinessNumber && companyName) {
			formik.setFieldValue('companyName', '');
		}
	}, [businessNumber, acnBusinessNumber, companyName]);

	// useLayoutEffect(() => {
	// 	if (ref.current && formik.values.companyName) {
	// 		console.log('Ref.current', ref.current, formik.values.companyName);
	// 		const inputElement = ref.current.querySelector('input');
	// 		if (inputElement) {
	// 			inputElement.setAttribute('value', formik.values.companyName);
	// 			// inputElement.value = formik.values.companyName;
	// 		}
	// 	}
	// }, []);

	return (
		<>
			<CreateQuoteSectionTitle
				title={
					searchType == null
						? 'Entity search'
						: searchType === ApiSearchTypes.ABN
						? 'Insured Entity or ABN Number'
						: 'Insured Entity or ACN Number'
				}
				tooltip={
					"This should be the Insured's main holding entity/trading entity. No need to enter any subsidiaries."
				}
			/>
			<Autocomplete
				// ref={ref}
				id='auto-select'
				key={`search-client-${resetKey}`}
				loading={searchLoading}
				loadingText='Loading companies ...'
				autoSelect={false}
				disableClearable={!!quoteGroup}
				disabled={readonly || currentQuotes.length > 0}
				options={clientList}
				// value={selectedOption}
				inputValue={searchText}
				clearOnBlur
				clearOnEscape={!quoteGroup}
				// defaultValue={clientList.find((v: any) => v?.identifier?.number === acnBusinessNumber || v?.businessNumber === businessNumber)}
				// options={clientsQuery.data}
				filterOptions={(x) => x}
				onKeyDown={(e) => {
					if (e.key === 'Enter') {
						e.preventDefault(); // To avoid any default behavior
						setShouldSearch(true); // Trigger the search
					}
				}}
				onInputChange={(_, value, reason) => {
					if (reason === 'input') {
						setSearchText(value);
						if (formik.values.companyName.length > 0) {
							clearSearch();
						}
					}
				}}
				// onInputChange={(_: any, value: any) => {
				// 	setSearchText(value);
				// 	if (formik.values.companyName.length > 0) {
				// 		clearSearch();
				// 	}
				// }}
				onBlur={() => {
					console.log('onBlur searchText.length', searchText.length);
					if (searchText.length === 0) {
						clearSearch();
					}
				}}
				getOptionDisabled={(option) =>
					option?.identifier?.number === acnBusinessNumber ||
					option?.businessNumber === businessNumber
				}
				renderOption={(props, option) => (
					<MenuItem
						{...props}
						key={option?.clientHash ?? option?.identifier?.number}
						// disabled={
						// 	option?.identifier?.number === acnBusinessNumber ||
						// 	option?.businessNumber === businessNumber
						// }
					>
						{option?.organizationName ??
							option?.fullName ??
							option?.name?.nameValue ??
							''}
					</MenuItem>
				)}
				getOptionLabel={(e: any) => {
					return e?.organizationName ?? e?.fullName ?? e?.name?.nameValue;
				}}
				isOptionEqualToValue={(option: any, value: any) =>
					option.businessNumber === value.businessNumber ||
					option.identifier?.number === value.identifier?.number
				}
				onChange={async (_, selectedValue) => {
					console.log('Selected value', selectedValue);

					if (selectedValue != null) {
						if (
							selectedValue.businessNumber !== businessNumber ||
							selectedValue.identifier?.number !== acnBusinessNumber
						) {
							formik.setFieldValue('businessNumber', selectedValue?.businessNumber);
							formik.setFieldValue(
								'acnBusinessNumber',
								selectedValue.identifier?.number
							);
							setClientList([]);
						} else {
							setClient(selectedValue);
						}
					} else {
						formik.setFieldValue('businessNumber', null);
						formik.setFieldValue('acnBusinessNumber', null);
						formik.setFieldValue('companyName', '');
					}
					// setClient(selectedValue);
				}}
				// value={searchText}
				renderInput={(params) => (
					<TextField
						// ref={ref}
						key={`text-${resetKey}`}
						error={formik.touched.companyName && Boolean(formik.errors.companyName)}
						placeholder={placeholder}
						{...params}
						// value={searchText}
						InputProps={{
							...params.InputProps,
							startAdornment: (
								<InputAdornment position='start'>
									<IconButton
										disabled={searchDisabled}
										size='small'
										sx={{
											backgroundColor: colours.darkGrey.main,
											'&:hover': {
												backgroundColor: colours.darkGrey.hover,
											},
										}}
										onClick={
											searchDisabled
												? undefined
												: () => {
														setClientList([]);
														setShouldSearch(true);
												  }
										}
									>
										<SearchIcon
											sx={{
												color: searchDisabled
													? 'rgba(0, 0, 0, 0.38)'
													: '#ffffff',
												cursor: searchDisabled ? 'not-allowed' : 'pointer',
											}}
										/>
									</IconButton>
								</InputAdornment>
							),
							endAdornment: (
								<InputAdornment position='end'>
									{searchLoading && (
										<CircularProgress
											size='1em'
											sx={{ color: colours.primary.main }}
										/>
									)}
								</InputAdornment>
							),
						}}
					/>
				)}
			/>
			<FormHelperText
				error={formik.touched.companyName && Boolean(formik.errors.companyName)}
			>
				{formik.touched.companyName && formik.errors.companyName}
			</FormHelperText>
		</>
	);
};

export default SearchCompanyField;
